import i18next from 'i18next';
import i18nextXHRBackend from 'i18next-xhr-backend';
import moment from 'moment/min/moment-with-locales';
import events from 'events';
import {setLocale as setYupLocale} from 'yup';

const LANGUAGE_CHANGE_EVENT = 'lang';
const DEFAULT_LANGUAGE = 'en';

const eventEmitter = new events.EventEmitter();
const defaultNamespaces = ['common'];
const [defaultNS, ...fallbackNS] = defaultNamespaces;

let i18nextInstance;

// Each <I18n> component will add a listener to this EventEmitter so that it will be notified of a language change.
// By default a warning is logged if more than 10 listeners are added.
// https://nodejs.org/docs/latest/api/events.html#events_emitter_setmaxlisteners_n
// Setting this higher so that we still get notified on excessive listeners,
// but at a number more than the number of translated strings that we expect to be on a page at once.
eventEmitter.setMaxListeners(1000);

export default {
  get i18next() {
    return i18nextInstance;
  },

  addListener: eventEmitter.addListener.bind(eventEmitter, LANGUAGE_CHANGE_EVENT),
  removeListener: eventEmitter.removeListener.bind(eventEmitter, LANGUAGE_CHANGE_EVENT),

  init(translations, namespaces = []) {
    function toTitleCase(str) {
      return str.replace(/\w\S*/g, function(txt) {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
      });
    }

    if (fallbackNS.length === 0) {
      fallbackNS.push(defaultNS);
    }

    const i18nextOptions = {
      lngs: [DEFAULT_LANGUAGE],
      fallbackLng: DEFAULT_LANGUAGE,
      ns: [...defaultNamespaces, ...namespaces],
      defaultNS,
      fallbackNS,
      resources: translations,
      interpolation: {
        format(value, format) {
          if (value instanceof Date) {
            return moment(value).format(format);
          } else if (value instanceof moment) {
            return value.format(format);
          } else if (format === 'downcase') {
            return value.toLowerCase();
          } else if (format === 'uppercase') {
            return value.toUpperCase();
          } else if (format === 'titlecase') {
            return toTitleCase(value);
          }
          return value;
        },
        escape(value) {
          return value;
        },
      },
      backend: {
        loadPath: `${process.env.PUBLIC_URL}/{{ns}}/locales/{{lng}}/app.json`,
      },
      react: {
        wait: true,
      },
    };

    i18nextInstance = i18next.createInstance();

    if (!translations) {
      i18nextInstance.use(i18nextXHRBackend);
    }

    i18nextInstance.on('languageChanged', function(lng) {
      moment.locale(lng);
      const yupTranslations = i18nextInstance.getResource(lng, 'common', 'model-validation');
      if (yupTranslations) {
        setYupLocale(yupTranslations);
      }
      eventEmitter.emit(LANGUAGE_CHANGE_EVENT, lng);
    });

    const fetchTranslation = (resolve, reject) => {
      try {
        i18nextInstance.init(i18nextOptions, () => {
          if (!translations) {
            i18nextInstance.loadLanguages([DEFAULT_LANGUAGE], () => {
              this.changeLanguage(i18nextOptions.lngs[0]);
              resolve();
            });
          } else {
            this.changeLanguage(i18nextOptions.lngs[0]);
            resolve();
          }
        });
      } catch (ex) {
        console.error('Failed to initialize i18n', ex);
        reject();
      }
    };

    return new Promise(fetchTranslation);
  },

  changeLanguage(lang) {
    return new Promise(resolve => {
      i18nextInstance.changeLanguage(lang, () => {
        resolve();
      });
    });
  },

  getLanguage() {
    return i18nextInstance?.language ?? DEFAULT_LANGUAGE;
  },

  t(...args) {
    return i18nextInstance?.t(...args) ?? args[0];
  },

  exists(...args) {
    return i18nextInstance?.exists(...args) ?? false;
  },

  addNamespace(namespace) {
    return new Promise(resolve => {
      i18nextInstance.loadNamespaces(namespace, resolve);
    });
  },
};
